import React, { useState } from 'react';
import { MEEM_BLOCKS, leituraDoBloco } from '../../lib/meemItems';
import { GLOSSARIO_NORMATIVO } from '../../lib/report';

const fmt = (n) => String(n).replace('.', ',');

/**
 * Leitura por bloco, opcional.
 *
 * Não aplica correção nem ajusta o escore: o total permanece o que foi somado.
 * É uma camada de leitura que o profissional liga se quiser, ciente da
 * limitação da base normativa declarada acima dela.
 */
export default function NormativeReading({ blockScores }) {
  const [ativo, setAtivo] = useState(false);

  const linhas = MEEM_BLOCKS.map((block) => {
    const obtido = blockScores[block.id] || 0;
    return { block, obtido, ...leituraDoBloco(block, obtido) };
  });

  const perdaDentroDoEsperado = linhas.filter((l) => l.situacao === 'perda-dentro-do-esperado').length;
  const abaixoFrequente = linhas.filter((l) => l.situacao === 'abaixo-em-bloco-de-perda-frequente').length;
  const abaixoIncomum = linhas.filter((l) => l.situacao === 'abaixo-em-bloco-habitualmente-integro').length;

  return (
    <section aria-labelledby="leitura-por-bloco" className="mt-8">
      <div className="border-t border-[var(--border-strong)] pt-6">
        <h4
          id="leitura-por-bloco"
          className="font-bold text-[var(--primary)] mb-3"
        >
          Limitação funcional por base de dados
        </h4>

        <p className="text-sm text-[var(--text-muted)] leading-relaxed mb-3">
          O desempenho por bloco desta calculadora é comparado a uma única amostra
          normativa brasileira: 185 indivíduos saudáveis avaliados nos subitens, dentro
          de um estudo de 433 pessoas, publicado em 2003, com recrutamento em dois
          locais. Não existe base normativa nacional posterior, de abrangência ampla e
          atualizada, para o MEEM por subitem. Escolaridade, contexto regional e perfil
          etário do Brasil de hoje não são os da amostra de origem.
        </p>

        <p className="text-sm text-[var(--text-muted)] leading-relaxed mb-4">
          Por isso esta leitura é <strong>opcional e descritiva</strong>. Ela não corrige
          nem ajusta a pontuação: o total continua sendo a soma dos itens marcados. A
          própria padronização de origem sustenta esse cuidado — os autores optaram
          deliberadamente por <strong>não fixar pontos de corte</strong>, porque o corte
          varia conforme a doença de base, e recomendaram que quem pontua abaixo da
          mediana do seu grupo de escolaridade seja avaliado quanto a perda funcional em
          relação ao nível prévio e encaminhado para avaliação mais detalhada.
        </p>

        <div className="mb-4 border-l-4 border-[var(--border-strong)] pl-4">
          <h5 className="font-bold text-sm text-[var(--primary)] mb-2">
            Máximo e mediana não são a mesma referência
          </h5>
          <p className="text-sm text-[var(--text-muted)] leading-relaxed">
            O <strong>máximo</strong> é o teto do bloco — quanto vale se tudo for acertado.
            A <strong>mediana</strong> é quanto pessoas saudáveis de fato pontuaram naquele
            bloco. Os dois raramente coincidem. No cálculo, por exemplo, o máximo é 5 e a
            mediana observada é 3: quem acerta 3 perdeu 2 pontos e mesmo assim pontuou na
            metade superior da amostra normativa. Perder ponto e ficar abaixo do esperado
            são coisas diferentes, e a coluna de perda existe para que uma não seja lida
            como a outra.
          </p>
          <p className="text-sm text-[var(--text-muted)] leading-relaxed mt-2">
            A mediana desta tabela é <strong>por bloco</strong>, e não se confunde com as
            medianas do escore total por escolaridade que aparecem na interpretação acima —
            aquelas comparam os 30 pontos somados, estas comparam um bloco isolado.
          </p>
        </div>

        <details className="mb-4 border-y border-[var(--border)]">
          <summary className="cursor-pointer p-3 text-sm font-bold text-[var(--primary)]">
            O que significam mediana, média, DP e o &quot;n&quot; da amostra
          </summary>
          <dl className="px-4 pb-4 space-y-3">
            {GLOSSARIO_NORMATIVO.map((item) => (
              <div key={item.termo}>
                <dt className="text-xs font-bold text-[var(--primary)]">{item.termo}</dt>
                <dd className="text-sm text-[var(--text-muted)] leading-relaxed mt-0.5">
                  {item.definicao}
                </dd>
              </div>
            ))}
          </dl>
        </details>

        <label className="flex gap-3 items-start cursor-pointer py-3 border-y border-[var(--border)]">
          <input
            type="checkbox"
            checked={ativo}
            onChange={() => setAtivo((v) => !v)}
            className="mt-1 w-5 h-5 shrink-0 accent-[var(--accent)]"
          />
          <span className="text-sm font-semibold text-[var(--primary)]">
            Considerar a variabilidade normativa por bloco
            <span className="block font-normal text-sm text-[var(--text-muted)] mt-0.5">
              Mostra, bloco a bloco, quanto foi perdido em relação ao máximo e como o
              resultado se posiciona diante do que pessoas saudáveis pontuaram.
            </span>
          </span>
        </label>

        {ativo && (
          <div className="mt-5">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <caption className="text-sm text-[var(--text-muted)] text-left pb-2">
                  Desempenho por bloco. As três colunas do meio descrevem a amostra
                  normativa de 185 pessoas saudáveis, não metas a atingir.
                </caption>
                <thead>
                  <tr>
                    <th className="p-3 border-b border-[var(--border)] font-bold">Bloco</th>
                    <th className="p-3 border-b border-[var(--border)] font-bold text-right">Obtido</th>
                    <th className="p-3 border-b border-[var(--border)] font-bold text-right">
                      Perdido<span className="block font-normal text-[10px]">até o máximo</span>
                    </th>
                    <th className="p-3 border-b border-[var(--border)] font-bold text-right">
                      Mediana<span className="block font-normal text-[10px]">saudáveis</span>
                    </th>
                    <th className="p-3 border-b border-[var(--border)] font-bold text-right">
                      Média (DP)<span className="block font-normal text-[10px]">desvio padrão</span>
                    </th>
                    <th className="p-3 border-b border-[var(--border)] font-bold text-right">
                      Mínimo<span className="block font-normal text-[10px]">na amostra</span>
                    </th>
                    <th className="p-3 border-b border-[var(--border)] font-bold">Leitura</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map(({ block, obtido, perdidos, texto }) => (
                    <tr key={block.id}>
                      <td className="p-3 border-b border-[var(--border)] font-semibold text-[var(--primary)]">
                        {block.label}
                      </td>
                      <td className="p-3 border-b border-[var(--border)] text-right font-bold">
                        {obtido} / {block.max}
                      </td>
                      <td className="p-3 border-b border-[var(--border)] text-right font-bold">
                        {perdidos > 0 ? `−${perdidos}` : '—'}
                      </td>
                      <td className="p-3 border-b border-[var(--border)] text-right">
                        {fmt(block.normativo.mediana)}
                      </td>
                      <td className="p-3 border-b border-[var(--border)] text-right text-[var(--text-muted)]">
                        {fmt(block.normativo.media)} ({fmt(block.normativo.dp)})
                      </td>
                      <td className="p-3 border-b border-[var(--border)] text-right text-[var(--text-muted)]">
                        {block.normativo.minObservado}
                      </td>
                      <td className="p-3 border-b border-[var(--border)] text-sm text-[var(--text-muted)] leading-relaxed">
                        {texto}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-sm text-[var(--text-muted)] leading-relaxed mt-5">
              Neste preenchimento: {perdaDentroDoEsperado} com perda que ainda fica igual ou
              acima da mediana da amostra, {abaixoFrequente} abaixo da mediana em bloco onde
              a perda é frequente entre pessoas saudáveis, e {abaixoIncomum} abaixo da
              mediana em bloco onde a amostra costuma acertar quase tudo. A contagem não
              classifica nem conclui nada — ela só impede que uma perda esperada seja lida
              com o mesmo peso de uma perda incomum.
            </p>

            <p className="text-sm text-[var(--text-muted)] italic leading-relaxed mt-3">
              Como a sinalização é decidida: um bloco entra na categoria de perda frequente
              quando sua média normativa fica abaixo de 90% do máximo do bloco. É uma regra
              derivada da tabela de subitens do estudo, aplicada igualmente a todos os
              blocos, e não um julgamento clínico item a item. Nenhuma dessas leituras
              substitui a avaliação de perda funcional em relação ao nível prévio da pessoa,
              que é o que a padronização de origem recomenda.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
